/** Canvas renderer only: no audio nodes, media ownership, or playback listeners. */
export function createSpectrumRenderer() {
  const bands = 32
  const segments = 20
  const peaks = new Float32Array(bands)
  const peakTimes = new Float64Array(bands)
  let previous = 0
  const levels = new Float32Array(bands)
  let palette: string[] = []
  let paletteAccent = ''

  return {
    reset() {
      peaks.fill(0)
      peakTimes.fill(0)
      levels.fill(0)
      previous = 0
    },
    draw(
      painter: CanvasRenderingContext2D,
      canvas: HTMLCanvasElement,
      frequencies: Uint8Array,
      sampleRate: number,
      now: number,
    ) {
      const width = canvas.clientWidth
      const height = canvas.clientHeight
      const accent =
        getComputedStyle(canvas).getPropertyValue('--vod-accent').trim() || '#f2cb55'
      if (accent !== paletteAccent) {
        paletteAccent = accent
        palette = Array.from({ length: segments }, (_, index) => {
          const half = segments / 2
          // Meet at the accent yellow: subdued below, bright amber above.
          const tint = index < half ? '#55503a' : '#ffb300'
          const mix =
            index < half
              ? 55 * (1 - index / (half - 1))
              : 100 * ((index - half) / (half - 1))
          return `color-mix(in srgb, ${accent} ${100 - mix}%, ${tint})`
        })
      }
      const left = 8
      const right = width - 8
      const top = 8
      const bottom = height - 26
      const pitch = (right - left) / bands
      const barWidth = Math.max(1, pitch - Math.max(1.5, pitch * 0.17))
      const segmentPitch = (bottom - top) / segments
      const segmentHeight = Math.max(1, segmentPitch - 1.5)
      // Start at the first resolvable FFT bin; no fictitious sub-bass bands.
      const binHz = sampleRate / (frequencies.length * 2)
      const low = Math.max(40, binHz)
      const high = Math.min(16000, sampleRate / 2)
      const ratio = high / low
      const elapsed = previous ? Math.min(1000, now - previous) : 0
      previous = now

      for (let band = 0; band < bands; band++) {
        const from = Math.max(1, Math.floor((low * ratio ** (band / bands)) / binHz))
        const to = Math.min(
          frequencies.length,
          Math.max(from + 1, Math.ceil((low * ratio ** ((band + 1) / bands)) / binHz)),
        )
        let value = 0
        for (let bin = from; bin < to; bin++)
          value = Math.max(value, frequencies[bin] / 255)
        // Immediate attack, gentle release, independent of the drawing frame rate.
        levels[band] = Math.max(value, levels[band] - elapsed / 500)
        if (levels[band] >= peaks[band]) {
          peaks[band] = levels[band]
          peakTimes[band] = now
        } else if (now - peakTimes[band] > 1000) {
          peaks[band] = Math.max(levels[band], peaks[band] - elapsed / 1000)
        }
        const lit = Math.ceil(levels[band] * segments)
        const peak = Math.ceil(peaks[band] * segments) - 1
        const x = left + band * pitch + (pitch - barWidth) / 2
        for (let segment = 0; segment < segments; segment++) {
          const on = segment < lit
          const marker = segment === peak && peak >= lit
          painter.globalAlpha = on ? 0.95 : marker ? 0.65 : 0.075
          painter.fillStyle = palette[segment]
          painter.fillRect(
            x,
            bottom - (segment + 1) * segmentPitch,
            barWidth,
            segmentHeight,
          )
        }
      }
      painter.globalAlpha = 1
      painter.fillStyle = accent
      painter.globalAlpha = 0.5
      painter.font = '10px ui-monospace, SFMono-Regular, monospace'
      painter.textBaseline = 'bottom'
      const ticks =
        width < 400 ? [low, 250, 1000, 4000, high] : [low, 100, 250, 1000, 4000, high]
      for (const hz of ticks) {
        const x = left + (Math.log(hz / low) / Math.log(ratio)) * (right - left)
        painter.textAlign = hz === low ? 'left' : hz === high ? 'right' : 'center'
        painter.fillText(
          hz >= 1000 ? `${Math.round(hz / 1000)}k` : `${Math.round(hz)}`,
          x,
          height - 2,
        )
      }
      painter.globalAlpha = 1
    },
  }
}
